import { useEffect, useRef } from "react";
import { usePlayer } from "@/contexts/PlayerContext";

// Singleton-style loader for the YouTube IFrame API
let ytApiPromise = null;
function loadYTApi() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    // Timeout: if API doesn't load in 15s, reject gracefully
    const timeout = setTimeout(() => {
      reject(new Error("YouTube IFrame API load timeout"));
    }, 15000);
    // Hook onto API ready
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      clearTimeout(timeout);
      if (typeof prev === "function") {
        try { prev(); } catch (_) { /* ignore */ }
      }
      resolve(window.YT);
    };
    if (!document.querySelector('script[data-yt-iframe-api]')) {
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      s.setAttribute("data-yt-iframe-api", "1");
      s.async = true;
      s.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("YouTube IFrame API script failed to load"));
      };
      document.body.appendChild(s);
    }
  });
  return ytApiPromise;
}

export default function YTAudioController() {
  // YT player error handler — propagate to PlayerContext via notifyYTError
  const { registerYTPlayer, notifyYTError } = usePlayer();
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadYTApi().then((YT) => {
      if (cancelled || !containerRef.current) return;
      // Render a tiny iframe but allow audio output
      playerRef.current = new YT.Player(containerRef.current, {
        height: "1",
        width: "1",
        host: "https://www.youtube.com",
        playerVars: {
          autoplay: 0,
          controls: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          fs: 0,
          iv_load_policy: 3,
          disablekb: 1,
        },
        events: {
          onReady: () => {
            registerYTPlayer(playerRef.current);
          },
          onError: (e) => {
            console.error("YT error", e?.data);
            if (typeof notifyYTError === "function") {
              notifyYTError(e?.data);
            }
          },
        },
      });
    }).catch((err) => {
      // YouTube API failed to load (ad blocker, network issue, timeout)
      console.warn("YouTube IFrame API unavailable:", err.message);
    });
    return () => { cancelled = true; };
  }, [registerYTPlayer, notifyYTError]);

  return (
    <div
      aria-hidden
      data-testid="yt-audio-host"
      style={{
        position: "fixed",
        bottom: 0, right: 0,
        width: 1, height: 1,
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
        overflow: "hidden",
      }}
    >
      <div ref={containerRef} />
    </div>
  );
}
